import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { url, axiosClient } from '@/api';
import { AxiosError } from 'axios';
import { ErrorObject, FiatCurrency, PaymentType } from '../common.types';

export interface IMerchantProfile {
  payoutType?: PaymentType;
  commerceDetailsId?: string;
  countryCode?: string;
  currency?: FiatCurrency;
  isActive?: boolean;
  isManuallyVerified?: boolean;
  accountName?: string;
  accountNumber?: string;
  bsb?: string;
  bankName?: string;
  bic?: string;
  iban?: string;
  aba?: string;
  clabe?: string;
  cnaps?: string;
  ifsc?: string;
  createdAt?: string;
}

export interface AccessKeysResponse {
  active: boolean;
  created: string;
  publicKey: string;
}

interface UpdateSettlementInfoPayload {
  key: string;
  value: string;
}

export function useGetSettlementInfo<TSelectData = IMerchantProfile, TError = AxiosError>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['GetSettlementInfo'],
    () => axiosClient.get(url.merchantDashboardController.settlementInfo),
    options,
  );
}

export function useUpdateSettlementInfo<TContext>(
  options?: Omit<
    UseMutationOptions<void, AxiosError<ErrorObject>, UpdateSettlementInfoPayload, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<void, AxiosError<ErrorObject>, UpdateSettlementInfoPayload, TContext> {
  const key = ['UpdateSettlementInfo'];

  return useMutation(
    (payload: UpdateSettlementInfoPayload) =>
      axiosClient.put(url.merchantDashboardController.settlementInfo, { [payload.key]: payload.value }),
    {
      ...options,
      mutationKey: key,
    },
  );
}

export function useGetAccessKeys<TSelectData = AccessKeysResponse, TError = AxiosError>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['GetAccessKeys'],
    () => axiosClient.get(url.merchantDashboardController.getAccessKeys),
    options,
  );
}

export function useCreateAccessKeys<TContext>(
  options?: Omit<
    UseMutationOptions<AccessKeysResponse, AxiosError<ErrorObject>, void, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<AccessKeysResponse, AxiosError<ErrorObject>, void, TContext> {
  const key = ['CreateAccessKeys'];

  return useMutation(() => axiosClient.post(url.merchantDashboardController.createAccessKeys), {
    ...options,
    mutationKey: key,
  });
}

export function useRevokeAccessKeys<TContext>(
  options?: Omit<
    UseMutationOptions<AccessKeysResponse, AxiosError<ErrorObject>, void, TContext>,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<AccessKeysResponse, AxiosError<ErrorObject>, void, TContext> {
  const key = ['RevokeAccessKeys'];

  return useMutation(() => axiosClient.post(url.merchantDashboardController.revokeAccessKeys), {
    ...options,
    mutationKey: key,
  });
}
export function useDownloadAccessKeys<TSelectData = AccessKeysResponse, TError = AxiosError>(
  options?: Omit<UseQueryOptions<void, TError, TSelectData>, 'queryKey' | 'queryFn'> | undefined,
): UseQueryResult<TSelectData, TError> {
  return useQuery<void, TError, TSelectData>(
    ['DownloadAccessKeys'],
    () => axiosClient.get(url.merchantDashboardController.downloadAccessKeys),
    options,
  );
}
